import * as SelectPrimitive from '@radix-ui/react-select';
import { forwardRef } from 'react';

import { cn } from '~/styles/extended-theme.ts';

import { Icon } from '../ui/icon.tsx';

export type SelectProps = SelectPrimitive.SelectProps & {
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
  triggerProps?: SelectPrimitive.SelectTriggerProps;
  contentPosition?: 'item-aligned' | 'popper';
  scrollOnOverflow?: boolean;
  iconProps?: string;
};

export type SelectOptionProps = SelectPrimitive.SelectItemProps;

const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      id,
      children,
      className,
      placeholder,
      defaultValue,
      triggerProps,
      contentPosition = 'item-aligned',
      scrollOnOverflow = false,
      iconProps,
      ...props
    },
    ref,
  ) => (
    <SelectPrimitive.Root {...props} defaultValue={defaultValue}>
      <SelectPrimitive.Trigger
        {...triggerProps}
        id={id}
        ref={ref}
        className={cn(
          'flex min-h-10 w-full border-b border-input bg-background px-1 py-2 text-sm ring-offset-background data-[placeholder]:text-primary-light',
          'hover:border-b-2',
          'focus-visible:border-b-4 focus-visible:border-double focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'overflow-hidden',
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <Icon name="chevron-down" className={cn('ml-auto h-4 w-4 opacity-50', iconProps)} />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position={scrollOnOverflow ? 'popper' : contentPosition}
          collisionPadding={scrollOnOverflow ? 10 : undefined}
          className="relative z-[100] min-w-[8rem] overflow-hidden rounded-md border bg-background text-primary shadow-md data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
        >
          <SelectPrimitive.Viewport
            className={cn(
              'w-full min-w-[var(--radix-select-trigger-width)] p-1',
              scrollOnOverflow
                ? 'h-[200px] overflow-y-scroll'
                : 'h-[var(--radix-select-trigger-height)]',
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  ),
);

const SelectItem = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm font-light outline-none hover:bg-neutral-200 focus:bg-neutral-200 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Icon name="check" className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
);

Select.displayName = SelectPrimitive.Select.displayName;
SelectItem.displayName = SelectPrimitive.Item.displayName;

export { Select, SelectItem };
